import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Megaphone } from 'lucide-react';
import { useAgency } from '@/auth/AgencyProvider';
import { useAnnouncementsUnread } from '@/data/useAnnouncements';

/** The badge text: hidden at 0, capped at "9+" (the name keeps the real number). */
export function announcementsBadge(n: number): string | null {
  if (!Number.isFinite(n) || n <= 0) return null;
  return n > 9 ? '9+' : String(n);
}

/**
 * fleet-driver-pay T5.3 (design 7.4): the megaphone in the top bar, before the bell. A link to the
 * announcements inbox with the unread count (`my_announcements_unread_count`, re-read on focus, every five
 * minutes and after a dismissal anywhere). Same badge style as the bell. Reads nothing until the agency is
 * ready, like the banner.
 */
export function AnnouncementsButton() {
  const { t } = useTranslation();
  const agency = useAgency();
  const { count } = useAnnouncementsUnread(agency.status === 'ready');
  const badge = announcementsBadge(count);
  return (
    <Link
      to="/announcements"
      aria-label={count > 0 ? t('announcements.button.unread', { count }) : t('announcements.button.label')}
      className="relative grid size-touch shrink-0 place-items-center rounded-md text-ink-secondary hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      <Megaphone className="size-5" aria-hidden="true" />
      {badge && (
        <span
          data-testid="announcements-badge"
          aria-hidden="true"
          className="tabular-figures absolute top-1 right-1 grid min-w-5 place-items-center rounded-full bg-destructive px-1 text-xs font-semibold text-destructive-foreground"
        >
          {badge}
        </span>
      )}
    </Link>
  );
}
